/**
 * ValidatrManager.ts - Universal validation system
 * 
 * CHANGE HISTORY:
 * - 251025_1105 [pending] danielbeer: Initial implementation as Zod wrapper
 * 
 * ARCHITECTURE NOTE:
 * This is a wrapper around Zod (https://zod.dev) with OpCentral-specific features:
 * - Schema registry for reusable validation schemas
 * - Integration with Loggr for validation failure tracking
 * - Integration with Actr for validation actions
 * - Validation statistics and monitoring
 * - Common OpCentral schemas (email, slug, etc.)
 * 
 * We use Zod because it's:
 * - Industry standard for TypeScript validation
 * - Type-safe (infers TypeScript types from schemas)
 * - Composable and extensible
 * - Well-maintained and battle-tested
 * 
 * Following OpCentral principle: Use external libraries when they do what we need,
 * extend them with OpCentral-specific features.
 */

import { z } from 'zod';
import type {
  ValidationResult,
  ValidationError,
  ValidationOptions,
  SchemaEntry,
  ValidationStats,
  ZodSchema,
} from './types';

/**
 * ValidatrManager - Universal validation system
 * 
 * Wraps Zod with OpCentral features:
 * - Schema registry
 * - Validation logging
 * - Statistics tracking
 * - Common schemas
 */
export class ValidatrManager {
  private schemas: Map<string, SchemaEntry> = new Map();
  private stats: ValidationStats = {
    totalValidations: 0,
    successfulValidations: 0,
    failedValidations: 0,
    averageValidationTime: 0,
    schemaUsage: {},
  };
  private loggr?: any;
  private actr?: any;

  constructor(options: { loggr?: any; actr?: any } = {}) {
    this.loggr = options.loggr;
    this.actr = options.actr;
    this.registerCommonSchemas();
  }

  /**
   * Register a validation schema
   */
  registerSchema(name: string, schema: ZodSchema, options: { description?: string; tags?: string[] } = {}): void {
    const now = Date.now();
    this.schemas.set(name, {
      name,
      schema,
      description: options.description,
      tags: options.tags,
      createdAt: now,
      updatedAt: now,
    });
  }

  /**
   * Get a registered schema
   */
  getSchema(name: string): ZodSchema | null {
    return this.schemas.get(name)?.schema || null;
  }

  /**
   * Validate data against a schema
   */
  async validate<T = any>(
    data: unknown,
    schemaOrName: ZodSchema<T> | string,
    options: ValidationOptions = {}
  ): Promise<ValidationResult<T>> {
    const startTime = performance.now();
    
    // Get schema
    let schema: ZodSchema<T>;
    let schemaName: string | undefined;
    
    if (typeof schemaOrName === 'string') {
      schemaName = schemaOrName;
      const registeredSchema = this.getSchema(schemaName);
      if (!registeredSchema) {
        throw new Error(`Schema "${schemaName}" not found in registry`);
      }
      schema = registeredSchema as ZodSchema<T>;
    } else {
      schema = schemaOrName;
    }

    // Validate
    try {
      const result = await schema.parseAsync(data);
      const duration = performance.now() - startTime;
      
      // Update stats
      this.updateStats(true, duration, schemaName);
      
      return {
        success: true,
        data: result,
        timestamp: Date.now(),
      };
    } catch (error) {
      const duration = performance.now() - startTime;
      
      // Convert Zod errors to our format
      const errors = this.convertZodErrors(error);
      
      // Update stats
      this.updateStats(false, duration, schemaName);
      
      // Log failure
      if (options.logFailures && this.loggr) {
        this.loggr.log('validation-failure', {
          schema: schemaName,
          errors,
          data,
        });
      }
      
      return {
        success: false,
        errors,
        timestamp: Date.now(),
      };
    }
  }

  /**
   * Validate data synchronously
   */
  validateSync<T = any>(
    data: unknown,
    schemaOrName: ZodSchema<T> | string,
    options: ValidationOptions = {}
  ): ValidationResult<T> {
    const startTime = performance.now();
    
    // Get schema
    let schema: ZodSchema<T>;
    let schemaName: string | undefined;
    
    if (typeof schemaOrName === 'string') {
      schemaName = schemaOrName;
      const registeredSchema = this.getSchema(schemaName);
      if (!registeredSchema) {
        throw new Error(`Schema "${schemaName}" not found in registry`);
      }
      schema = registeredSchema as ZodSchema<T>;
    } else {
      schema = schemaOrName;
    }

    // Validate
    try {
      const result = schema.parse(data);
      const duration = performance.now() - startTime;
      
      // Update stats
      this.updateStats(true, duration, schemaName);
      
      return {
        success: true,
        data: result,
        timestamp: Date.now(),
      };
    } catch (error) {
      const duration = performance.now() - startTime;
      
      // Convert Zod errors to our format
      const errors = this.convertZodErrors(error);
      
      // Update stats
      this.updateStats(false, duration, schemaName);
      
      // Log failure
      if (options.logFailures && this.loggr) {
        this.loggr.log('validation-failure', {
          schema: schemaName,
          errors,
          data,
        });
      }
      
      return {
        success: false,
        errors,
        timestamp: Date.now(),
      };
    }
  }

  /**
   * Check if data is valid (returns boolean)
   */
  isValid<T = any>(data: unknown, schemaOrName: ZodSchema<T> | string): boolean {
    const result = this.validateSync(data, schemaOrName);
    return result.success;
  }

  /**
   * Get validation statistics
   */
  getStats(): ValidationStats {
    return { ...this.stats };
  }

  /**
   * Reset validation statistics
   */
  resetStats(): void {
    this.stats = {
      totalValidations: 0,
      successfulValidations: 0,
      failedValidations: 0,
      averageValidationTime: 0,
      schemaUsage: {},
    };
  }

  /**
   * List all registered schemas
   */
  listSchemas(): SchemaEntry[] {
    return Array.from(this.schemas.values());
  }

  /**
   * Remove a schema from registry
   */
  unregisterSchema(name: string): boolean {
    return this.schemas.delete(name);
  }

  // ==================== PRIVATE METHODS ====================

  /**
   * Convert Zod errors to our format
   */
  private convertZodErrors(error: any): ValidationError[] {
    if (error instanceof z.ZodError) {
      return error.errors.map(err => ({
        path: err.path,
        message: err.message,
        code: err.code,
        expected: (err as any).expected,
        received: (err as any).received,
      }));
    }
    
    return [{
      path: [],
      message: error.message || 'Validation failed',
      code: 'unknown',
    }];
  }

  /**
   * Update validation statistics
   */
  private updateStats(success: boolean, duration: number, schemaName?: string): void {
    this.stats.totalValidations++;
    
    if (success) {
      this.stats.successfulValidations++;
    } else {
      this.stats.failedValidations++;
    }
    
    // Update average time
    const totalTime = this.stats.averageValidationTime * (this.stats.totalValidations - 1) + duration;
    this.stats.averageValidationTime = totalTime / this.stats.totalValidations;
    
    // Update schema usage
    if (schemaName) {
      this.stats.schemaUsage[schemaName] = (this.stats.schemaUsage[schemaName] || 0) + 1;
    }
  }

  /**
   * Register common OpCentral schemas
   */
  private registerCommonSchemas(): void {
    // Email validation
    this.registerSchema('email', z.string().email(), {
      description: 'Valid email address',
      tags: ['common', 'user'],
    });

    // Slug validation (lowercase, alphanumeric, hyphens)
    this.registerSchema('slug', z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/), {
      description: 'URL-safe slug (lowercase, alphanumeric, hyphens)',
      tags: ['common', 'url'],
    });

    // UUID validation
    this.registerSchema('uuid', z.string().uuid(), {
      description: 'Valid UUID',
      tags: ['common', 'id'],
    });

    // URL validation
    this.registerSchema('url', z.string().url(), {
      description: 'Valid URL',
      tags: ['common', 'url'],
    });

    // Phone number (basic - requires at least 10 digits)
    this.registerSchema('phone', z.string().regex(/^\+?[1-9]\d{9,14}$/), {
      description: 'Valid phone number (E.164 format, min 10 digits)',
      tags: ['common', 'user'],
    });

    // Date string (ISO 8601)
    this.registerSchema('dateString', z.string().datetime(), {
      description: 'ISO 8601 date string',
      tags: ['common', 'date'],
    });

    // Non-empty string
    this.registerSchema('nonEmptyString', z.string().min(1), {
      description: 'Non-empty string',
      tags: ['common'],
    });

    // Positive integer
    this.registerSchema('positiveInt', z.number().int().positive(), {
      description: 'Positive integer',
      tags: ['common', 'number'],
    });

    // Percentage (0-100)
    this.registerSchema('percentage', z.number().min(0).max(100), {
      description: 'Percentage (0-100)',
      tags: ['common', 'number'],
    });
  }
}

// Export Zod for direct use when needed
export { z };
